import React, { useEffect } from "react";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import BrandAdContainer from "web/react/components/brand-ad-container/brand-ad-container";
import Breadcrumbs from "web/react/components/breadcrumbs/breadcrumbs";
import ProductDescription from "web/react/components/buybuybuy-area/product-description/product-description";
// Components
import CustomerCaptureOverlay from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import OOSProductHero from "web/react/components/oos-product-hero/oos-product-hero";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import ProductRetailers from "web/react/components/product-retailers/product-retailers";
import ProductsCarousel from "web/react/components/products-carousel/products-carousel";
import RecentlyViewed from "web/react/components/recently-viewed/recently-viewed";
import RelatedDesignersRail from "web/react/components/related-designers/related-designers-rail";
import RelatedProductsArea from "web/react/components/related-products-area/related-products-area";
import RelatedSearchesRail from "web/react/components/related-searches-rail/related-searches-rail";
import SaveForLaterSendToAppModal from "web/react/components/save-for-later-send-to-app-modal/save-for-later-send-to-app-modal";
// Styles
// eslint-disable-next-line css-modules/no-unused-class
import styles from "web/react/pages/product/out-of-stock/out-of-stock/out-of-stock.css";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import {
    OOSAffiliateProductLayoutSerializer,
    OOSIconProductLayoutSerializer,
} from "web/types/serializers";

interface OutOfStockProductPageProps {
    productLayout: OOSAffiliateProductLayoutSerializer | OOSIconProductLayoutSerializer;
    productUrl: string;
    helpCentreURLs: string[];
    showCookieSettingsOption: boolean;
    country: string;
    variant: "affiliate" | "icon";
    labels?: Record<string, any>;
}

function OutOfStockProductPage({
    productLayout,
    productUrl,
    helpCentreURLs,
    showCookieSettingsOption,
    country,
    variant,
    labels,
}: OutOfStockProductPageProps): React.ReactElement {
    const {
        customer_capture_data: customerCaptureData,
        product_analytics_data: productAnalyticsData,
        brand_ad: brandAd,
        curated_ads: curatedAds,
        product_hero_card: productHeroCard,
        adsense_for_shopping: adsenseForShopping,
        related_products: relatedProducts,
        related_search_rail: relatedSearchRail,
        product_breadcrumbs: productBreadcrumbs,
        product_description: productDescription,
        product_more_descriptions: productMoreDescriptions,
        product_image_gallery: productImageGallery,
        product_retailers: productRetailers,
        recently_viewed_products: recentlyViewedProducts,
        footer,
        features,
    } = productLayout;
    const {
        related_designers_type_rail: relatedDesignersTypeRail,
        related_designers_category_rail: relatedDesignersCategoryRail,
        related_designers_subcategory_rail: relatedDesignersSubcategoryRail,
    } = productLayout as OOSAffiliateProductLayoutSerializer;

    const isIcon = variant === "icon";
    const isAffiliate = variant === "affiliate";

    useEffect(() => {
        analytics.tagEvent(analytics.TAG_NETWORKS.FACEBOOK, facebookEvents.VIEW_CONTENT, {
            content_ids: [(productAnalyticsData as any).product_id],
            content_type: "product",
        });

        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    return (
        <div className={styles.container}>
            <CustomerCaptureOverlay {...customerCaptureData} />
            <SaveForLaterSendToAppModal />
            <div className={styles.content}>
                {brandAd && isIcon && brandAd.variant === "above" && (
                    <BrandAdContainer {...brandAd} />
                )}

                {productHeroCard && (
                    <OOSProductHero
                        variant={variant}
                        data={productHeroCard}
                        productUrl={productUrl}
                        labels={labels}
                    />
                )}

                {brandAd && (isAffiliate || (isIcon && brandAd.variant === "below")) && (
                    <BrandAdContainer {...brandAd} />
                )}

                {adsenseForShopping && (
                    <div className="mb20">
                        <AdsenseShopping {...adsenseForShopping} />
                    </div>
                )}

                {curatedAds && (
                    <ProductsCarousel title={curatedAds.title} products={curatedAds.products} />
                )}

                {relatedProducts && (
                    <div className="mb10 mt10">
                        <RelatedProductsArea
                            sections={relatedProducts.sections}
                            noBrandAd={!!brandAd}
                            noAdsense={!!adsenseForShopping}
                            hideShortDescription={environment.getFeature(
                                "seo_hide_desc_related_oos_pdp"
                            )}
                            onOOS
                            isAffiliate={isAffiliate}
                        />
                    </div>
                )}

                {isAffiliate && (
                    <>
                        {!!relatedDesignersTypeRail?.designers?.length && (
                            <RelatedDesignersRail
                                title={relatedDesignersTypeRail.title}
                                designers={relatedDesignersTypeRail.designers}
                                features={features}
                            />
                        )}

                        {!!relatedDesignersCategoryRail?.designers?.length && (
                            <RelatedDesignersRail
                                title={relatedDesignersCategoryRail.title}
                                designers={relatedDesignersCategoryRail.designers}
                                features={features}
                            />
                        )}

                        {!!relatedDesignersSubcategoryRail?.designers?.length && (
                            <RelatedDesignersRail
                                title={relatedDesignersSubcategoryRail.title}
                                designers={relatedDesignersSubcategoryRail.designers}
                                features={features}
                            />
                        )}
                    </>
                )}

                {relatedSearchRail && !!relatedSearchRail.related_searches.length && (
                    <RelatedSearchesRail
                        title={gettext("product.related_products.related_searches")}
                        relatedSearches={relatedSearchRail?.related_searches}
                        features={features as any}
                    />
                )}

                {productBreadcrumbs && (
                    <Breadcrumbs type="pdp" breadcrumbs={productBreadcrumbs?.breadcrumbs} />
                )}

                {productDescription && (
                    <div className={styles.productDescription}>
                        <div className="page-block p30 pt10">
                            <ProductDescription
                                title={productDescription.title}
                                description={productDescription.description}
                                read_more_limit={productDescription.read_more_limit}
                                product_sizes={productDescription.product_sizes}
                                more_descriptions={productMoreDescriptions}
                                without_scroll
                            />
                            <h4 className="mt20">
                                {gettext("oos_product.product_details_photos_title")}
                            </h4>
                            <div className="mb0">
                                <PDPGallery
                                    variant="oos-carousel"
                                    hasRelatedProductsSidebar={false}
                                    product={productImageGallery}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="m20 mt10 mb10">
                    <ProductRetailers
                        retailers={productRetailers.retailers}
                        previously_sold_at_retailers_html={
                            productRetailers.previously_sold_at_retailers_html
                        }
                    />
                </div>

                <div className="mb40">
                    <RecentlyViewed
                        title={recentlyViewedProducts.title}
                        products={recentlyViewedProducts.products}
                        is_loading={recentlyViewedProducts.is_loading}
                        current_product_id={recentlyViewedProducts.current_product_id}
                        in_overlay={recentlyViewedProducts.in_overlay}
                    />
                </div>
            </div>

            <AppFooter
                data={footer}
                helpCenterUrls={helpCentreURLs}
                showCookieSettingsOption={showCookieSettingsOption}
                country={country}
            />
        </div>
    );
}

export default OutOfStockProductPage;
